import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

export const uid = () => Math.random().toString(36).slice(2, 10);

export const fmtDateTimeLocal = (d, includeSeconds = false) => {
  const date = new Date(d);
  const tzOffset = date.getTimezoneOffset() * 60000;
  const local = new Date(date.getTime() - tzOffset);
  return local.toISOString().slice(0, includeSeconds ? 19 : 16);
};

export const parseDateTimeLocal = (s) => {
  if (!s) return new Date();
  const [datePart, timePart = '00:00'] = s.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second = 0] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hour, minute, second);
};
export const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const startOfWeek = (d) => {
  const date = startOfDay(d);
  const dayIndex = date.getDay();
  const diff = (dayIndex + 6) % 7;
  return addDays(date, -diff);
};
export const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
export const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
export const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

export function rruleLabel(rule) {
  if (!rule) return 'One-off';
  if (rule.startsWith('FREQ=DAILY')) return 'Daily';
  if (rule.startsWith('FREQ=WEEKLY')) return 'Weekly';
  if (rule.startsWith('FREQ=MONTHLY')) return 'Monthly';
  if (rule.startsWith('FREQ=YEARLY')) return 'Yearly';
  return 'Custom';
}

export const titleCase = (s = '') =>
  s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase());

export function Badge({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1 ring-white/20 ${className}`}>
      {children}
    </span>
  );
}

export function Modal({ open, onClose, children, title, actions = null, panelClassName = '' }) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-50 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/60" onClick={onClose} />
          <div className="relative flex min-h-full items-start justify-center p-4 sm:p-6">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              className={`relative my-4 w-[min(960px,100%)] max-h-[calc(100vh-2rem)] overflow-auto rounded-2xl bg-slate-900 p-6 text-slate-100 ring-1 ring-white/10 sm:my-6 sm:max-h-[calc(100vh-3rem)] ${panelClassName}`}
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="min-w-0 text-lg font-semibold">{title}</h3>
                <div className="flex items-center gap-2">
                  {actions}
                  <button onClick={onClose} className="rounded-xl p-2 hover:bg-white/10">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              {children}
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function SectionCard({ title, icon: Icon, children, actions }) {
  return (
    <div className="p-4 rounded-2xl bg-white/5 ring-1 ring-white/10">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon ? (
            <div className="p-2 rounded-xl bg-white/5 ring-1 ring-white/10">
              <Icon className="w-4 h-4" />
            </div>
          ) : null}
          <h3 className="font-semibold">{title}</h3>
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}
